import {useCallback} from 'react';
import {useAppDispatch, useAppSelector} from '../store/hooks';
import {updateVideoEditorPreference} from '../store/slices/authThunks';
import {
  videoEditorSelector,
  updateVideoEditorPreferenceApiSelector,
} from '../store/selectors/auth.selectors';
import type {VideoEditor} from '../backend-types/user';

/**
 * Drives the video editor picker: welcome.container.tsx, account.container.tsx and
 * videoEditorGate.container.tsx all use this rather than duplicating the PATCH, since the question
 * is asked in three places and answered the same way in all of them.
 *
 * Every pick saves immediately — there is no confirm step for any editor. There used to be one for
 * DaVinci Resolve, on the belief that it hangs on a media reference it cannot relink; it doesn't,
 * so the dialog is gone and the answer is now just a preference like any other.
 */
export function useVideoEditorPreference() {
  const dispatch = useAppDispatch();
  const videoEditor = useAppSelector(videoEditorSelector);
  const api = useAppSelector(updateVideoEditorPreferenceApiSelector);

  const choose = useCallback((editor: VideoEditor) => {
    dispatch(updateVideoEditorPreference({videoEditor: editor}));
  }, [dispatch]);

  const clear = useCallback(() => {
    dispatch(updateVideoEditorPreference({videoEditor: null}));
  }, [dispatch]);

  return {
    videoEditor,
    choose,
    clear,
    isSaving: api.status === 'pending',
  };
}
